import React, { useEffect, useState } from "react";
import { DashboardStats } from "../../types/admin";
import AdminNav from "../../components/AdminNav";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE_URL}/api/admin/dashboard/stats`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("統計情報の取得に失敗しました");
      }

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <>
      <AdminNav />
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-8">管理者ダッシュボード</h1>

        {/* ユーザー統計 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">ユーザー統計</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="総ユーザー数"
              value={stats.users.total}
              icon="👥"
              color="blue"
            />
            <StatCard
              title="アクティブユーザー"
              value={stats.users.active}
              subtitle="(過去7日間)"
              icon="✅"
              color="green"
            />
            <StatCard
              title="新規ユーザー"
              value={stats.users.new_last_7_days}
              subtitle="(過去7日間)"
              icon="🆕"
              color="purple"
            />
            <StatCard
              title="新規ユーザー"
              value={stats.users.new_last_30_days}
              subtitle="(過去30日間)"
              icon="📈"
              color="indigo"
            />
          </div>
        </section>

        {/* 企業統計 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">企業統計</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="総企業数"
              value={stats.companies.total}
              icon="🏢"
              color="blue"
            />
            <StatCard
              title="トラッキング中のユーザー"
              value={stats.companies.tracked_by_users}
              icon="👤"
              color="green"
            />
          </div>
        </section>

        {/* 応募統計 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">応募統計</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="総応募数"
              value={stats.applications.total}
              icon="📝"
              color="blue"
            />
            <StatCard
              title="進行中"
              value={stats.applications.in_progress}
              icon="⏳"
              color="yellow"
            />
            <StatCard
              title="完了"
              value={stats.applications.completed}
              icon="✔️"
              color="green"
            />
          </div>
        </section>

        {/* お問い合わせ統計 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">お問い合わせ統計</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="総お問い合わせ"
              value={stats.contact_submissions.total}
              icon="📧"
              color="blue"
            />
            <StatCard
              title="新規"
              value={stats.contact_submissions.new}
              icon="🔔"
              color="red"
              highlight={stats.contact_submissions.new > 0}
            />
            <StatCard
              title="対応中"
              value={stats.contact_submissions.in_progress}
              icon="⚙️"
              color="yellow"
            />
            <StatCard
              title="解決済み"
              value={stats.contact_submissions.resolved}
              icon="✅"
              color="green"
            />
          </div>
        </section>
      </div>
    </>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: string;
  color: "blue" | "green" | "purple" | "indigo" | "yellow" | "red";
  highlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  highlight = false,
}) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200",
    green: "bg-green-50 border-green-200",
    purple: "bg-purple-50 border-purple-200",
    indigo: "bg-indigo-50 border-indigo-200",
    yellow: "bg-yellow-50 border-yellow-200",
    red: "bg-red-50 border-red-200",
  };

  return (
    <div
      className={`${colorClasses[color]} border rounded-lg p-6 ${
        highlight ? "ring-2 ring-red-400 animate-pulse" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-3xl">{icon}</span>
        <span className="text-3xl font-bold">{value}</span>
      </div>
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
};

export default AdminDashboard;
